import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Container, Row, Col, Card, Spinner } from 'react-bootstrap';
import Breadcrumbs from '../../Common/Breadcrumbs';
import profilePhoto from '../../assets/images/profile photo.png';
import './TournamentDetail.css';
import { getTournamentById, Tournament as ApiTournament } from '../../api/clubmanager/tournamentService';

const TournamentDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const stateTournament = (location.state as any)?.tournament as ApiTournament | undefined;

  const [tournament, setTournament] = useState<ApiTournament | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const load = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        
        // Try to load from API first
        const data = await getTournamentById(id);
        setTournament(data);
      } catch (e: any) {
        console.error('Failed to load tournament details from API:', e);
        
        // If API fails, try to use state data if available
        if (stateTournament) {
          setTournament(stateTournament);
        } else {
          // If no state data, show error state instead of mock data
          console.error('No tournament data available from API or navigation state');
          setTournament(null);
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, stateTournament]);

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, '0');
      const month = date.toLocaleDateString('en-US', { month: 'short' });
      const year = date.getFullYear().toString().slice(-2);
      return `${day} ${month} ${year}`;
    } catch (error) {
      return '';
    }
  };

  const handleBack = () => {
    navigate('/tournaments');
  };

  const handleRegisterNow = () => {
    if (!tournament) return;
    navigate(`/tournaments/${tournament._id}/register`, { 
      state: { 
        tournament: tournament,
        clubDetails: {
          logo: tournament.logo || profilePhoto,
          name: 'Regal Sports FC',
          teamName: 'Rising Stars FC',
          teamShortName: 'RSFC',
          coachManagerName: 'Ramesh Kumar',
          mobileNumber: '9826789123',
          email: 'regalsports342@gmail.com',
          location: tournament.location || 'JP Nagar, Bangalore'
        }
      }
    });
  };


  const headerStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '24px',
    padding: '0 15px',
    minHeight: '60px'
  };

  const logoStyle = {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    overflow: 'hidden',
    background: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '2px solid #e9ecef',
    flexShrink: 0
  } as const;

  const titleSectionStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flex: 1
  } as const;

  const badgeStyle = {
    fontFamily: 'Helvetica Neue, Arial, sans-serif',
    fontWeight: 600,
    fontSize: '12px',
    lineHeight: '16px',
    padding: '6px 12px',
    borderRadius: '20px',
    border: '1px solid #1976d2',
    background: '#e3f2fd',
    color: '#1976d2',
    display: 'inline-block',
    minWidth: '80px',
    textAlign: 'center' as const
  };

  const ageBadgeStyle = {
    fontFamily: 'Helvetica Neue, Arial, sans-serif',
    fontWeight: 500,
    fontSize: '12px',
    lineHeight: '16px',
    padding: '6px 12px',
    borderRadius: '20px',
    background: '#d4edda',
    color: '#155724',
    border: 'none',
    display: 'inline-block',
    minWidth: '40px',
    textAlign: 'center' as const,
    marginRight: '8px'
  };

  const registerButtonStyle = {
    background: '#006EFF',
    border: 'none',
    color: '#fff',
    fontFamily: 'Inter, Arial, sans-serif',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '20px',
    padding: '12px 24px',
    borderRadius: '8px',
    cursor: 'pointer',
    minWidth: '140px',
    boxShadow: '0 2px 4px rgba(0, 110, 255, 0.2)',
    display: 'block'
  } as const;

  if (loading) {
    return (
      <div className="tournament-detail-page" style={{ marginTop: '20px' }}>
        <Container fluid>
          <div className="loading-container">
            <Spinner animation="border" role="status" />
          </div>
        </Container>
      </div>
    );
  }

  // For now, suppress API error display to keep mock/state visible
  if (!tournament) {
    return (
      <div className="tournament-detail-page" style={{ marginTop: '20px' }}>
        <Container fluid>
          <div style={{ padding: '0 15px', marginBottom: '8px' }}>
            <Breadcrumbs 
              items={[
                { label: 'Tournaments', onClick: () => navigate('/tournaments') },
                { label: 'Tournament Not Found', active: true }
              ]} 
            />
          </div>
          <div style={{ textAlign: 'center', padding: '50px 20px' }}>
            <h3>Tournament Not Found</h3>
            <p>The tournament you're looking for could not be found or is no longer available.</p>
            <button 
              className="btn btn-primary"
              onClick={() => navigate('/tournaments')}
            >
              Back to Tournaments
            </button>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="tournament-detail-page" style={{ marginTop: '20px', backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      <Container fluid>


        {/* Breadcrumbs */}
        <div style={{ padding: '0 15px', marginBottom: '8px' }}>
          <Breadcrumbs 
            items={[
              { label: 'Tournaments', onClick: () => navigate('/tournaments') },
              { label: 'Upcoming Tournaments Listing', onClick: () => navigate('/tournaments') },
              { label: `${tournament.name} Detail`, active: true }
            ]} 
          />
        </div>

        {/* Tournament Header */}
        <div style={headerStyle}>
          <button 
            className="back-button"
            onClick={handleBack}
            style={{ position: 'relative', zIndex: 10 }}
          >
            <i className="ri-arrow-left-line"></i>
          </button>
          <div style={logoStyle}>
            <img 
              src={tournament.logo || profilePhoto}
              alt="Tournament Logo"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = profilePhoto;
              }}
            />
          </div>
          <div style={titleSectionStyle}>
            <h1 className="tournament-title">{tournament.name}</h1>
            <span style={badgeStyle}>
              {(tournament.status || 'upcoming').charAt(0).toUpperCase() + (tournament.status || 'upcoming').slice(1)}
            </span>
          </div>
        </div>

        {/* Tournament Details Card */}
        <Card className="tournament-details-card">
          <Card.Body>
            <h2 className="details-title">Tournament Details</h2>
            <div className="details-content">
              {/* First Row */}
              <Row className="detail-row">
                <Col md={6} className="detail-column">
                  <div className="detail-item">
                    <label className="detail-label">Tournament Logo*</label>
                    <div className="detail-value logo-value">
                      <img 
                        src={tournament.logo || profilePhoto} 
                        alt="Tournament Logo" 
                        className="team-logo"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = profilePhoto;
                        }}
                      />
                    </div>
                  </div>
                </Col>
                <Col md={6} className="detail-column">
                  <div className="detail-item">
                    <label className="detail-label">Tournament Name</label>
                    <div className="detail-value">{tournament.name}</div>
                  </div>
                </Col>
              </Row>

              {/* Second Row */}
              <Row className="detail-row">
                <Col md={6} className="detail-column">
                  <div className="detail-item">
                    <label className="detail-label">Date</label>
                    <div className="detail-value">{formatDate(tournament.startDate)}</div>
                  </div>
                </Col>
                <Col md={6} className="detail-column">
                  <div className="detail-item">
                    <label className="detail-label">Sports</label>
                    <div className="detail-value">{tournament.sports || '-'}</div>
                  </div>
                </Col>
              </Row>

              {/* Third Row */}
              <Row className="detail-row">
                <Col md={6} className="detail-column">
                  <div className="detail-item">
                    <label className="detail-label">Age Category</label>
                    <div className="detail-value">
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', minHeight: '30px' }}>
                        {(tournament.ageCategories || []).map((category, index) => (
                          <span key={index} style={ageBadgeStyle}>
                            {category}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </Col>
                <Col md={6} className="detail-column">
                  <div className="detail-item">
                    <label className="detail-label">Venue</label>
                    <div className="detail-value venue-value">{tournament.location || '-'}</div>
                  </div>
                </Col>
              </Row>
            </div>
          </Card.Body>
        </Card>

        {/* Register Now Button */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '15px 15px 0 15px', marginTop: '0', minHeight: '60px' }}>
          <button 
            style={{
              ...registerButtonStyle,
              backgroundColor: tournament.isTeamRegistered ? '#1A1A1A' : '#006EFF',
              color: tournament.isTeamRegistered ? '#FFFFFF' : '#fff',
              borderColor: tournament.isTeamRegistered ? '#1A1A1A' : undefined,
              boxShadow: tournament.isTeamRegistered ? '0 2px 4px rgba(26, 26, 26, 0.2)' : '0 2px 4px rgba(0, 110, 255, 0.2)'
            }} 
            onClick={handleRegisterNow}
            onMouseEnter={(e) => {
              if (tournament.isTeamRegistered) {
                e.currentTarget.style.backgroundColor = '#1A1A1A';
                e.currentTarget.style.color = '#FFFFFF';
                e.currentTarget.style.borderColor = '#1A1A1A';
              }
            }}
            onMouseLeave={(e) => {
              if (tournament.isTeamRegistered) {
                e.currentTarget.style.backgroundColor = '#1A1A1A';
                e.currentTarget.style.color = '#FFFFFF';
                e.currentTarget.style.borderColor = '#1A1A1A';
              }
            }}
          >
            {tournament.isTeamRegistered ? 'Manage' : 'Register Now'}
          </button>
        </div>
      </Container>
    </div>
  );
};

export default TournamentDetail; 